import { useState, useEffect } from "react";
import {
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
  IconButton,
  Paper,
  Grid,
  Avatar,
  CircularProgress,
  InputAdornment,
  Collapse,
  Fade,
  Checkbox,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Alert } from "@material-ui/lab";
import {
  Business,
  Person,
  AccountBalance,
  CloudUpload,
  Delete,
  Check,
  VisibilityOff,
  VerifiedUser,
  AssignmentInd,
  Info,
  CheckCircle,
  Save,
  Restore,
  Close,
  Build,
} from "@material-ui/icons";
import HttpService from "../../services/httpService";

// ─── Design tokens (identical to RentalOwnerOnboarding) ──────────────────────
const PRIMARY         = "#1A3C6E";
const PRIMARY_LIGHT   = "#2E75B6";
const PRIMARY_LIGHTER = "#EBF3FB";
const SUCCESS_COLOR   = "#1B8A5A";
const WARN_COLOR      = "#B45309";
const WARN_BG         = "#FFFBEB";
const WARN_BORDER     = "#FCD34D";
const BORDER          = "#C8D8EC";

// ─── Service types (fallback only — the live list comes from the admin-
// managed Service Type config; see fetchServiceTypeOptions below) ────────────
const FALLBACK_SERVICE_TYPES = [
  { value: "stationary",     label: "Stationary" },
  { value: "porter",         label: "Porter" },
  { value: "housekeeping",   label: "Housekeeping" },
  { value: "ccd",            label: "CCD" },
  { value: "drinking_water", label: "Drinking Water" },
  { value: "others",         label: "Others" },
];

const ACCOUNT_TYPES = ["savings", "current", "nro", "nre"];

const BLANK_SCOPE = { service_type: "", service_type_other: "" };
const BLANK_LOCATION = { experienceCenterId: "", scopes: [{ ...BLANK_SCOPE }] };

// ─── Initial state ────────────────────────────────────────────────────────────
const INITIAL_SP = {
  location_mappings: [{ ...BLANK_LOCATION, scopes: [{ ...BLANK_SCOPE }] }],
  name: "",
  email: "",
  mobile: "",
  pan_number: "",
  aadhaar: "",
  gst_applicable: false,
  gst_number: "",
  tds_applicable: false,
  tds_percentage: "",
  bank: {
    account_number: "", account_name: "", bank_name: "",
    branch_name: "", ifsc_code: "", account_type: "savings",
  },
  files: {
    aadhaar_doc: null,
    pan_doc: null,
    cheque_doc: null,
  },
};

// ─── Validation ───────────────────────────────────────────────────────────────
function validateSP(form, isEditMode = false) {
  const e = {};
  if (!form.location_mappings || form.location_mappings.length === 0) {
    e.location_mappings = "Add at least one location";
  } else {
    form.location_mappings.forEach((loc, i) => {
      if (!loc.experienceCenterId) e[`loc_${i}_ec`] = "Select a location";
      if (!loc.scopes || loc.scopes.length === 0 || !loc.scopes.some((s) => s.service_type)) {
        e[`loc_${i}_scope`] = "Select at least one scope for this location";
      }
      (loc.scopes || []).forEach((s, j) => {
        if (s.service_type === "others" && !s.service_type_other?.trim()) {
          e[`loc_${i}_scope_${j}_other`] = "Please specify the service type";
        }
      });
    });
  }
  if (!form.name.trim())           e.name               = "Name is required";
  if (!form.email.trim())          e.email              = "Email is required";
  if (!/^[6-9]\d{9}$/.test(form.mobile)) e.mobile = "Valid 10-digit mobile required";

  if (!form.pan_number.trim()) e.pan_number = "PAN is required";
  else if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(form.pan_number.toUpperCase()))
    e.pan_number = "Invalid PAN format (e.g. ABCDE1234F)";

  // Aadhaar is immutable once onboarded — not editable/re-enterable in edit mode.
  const rawAadhaar = form.aadhaar.replace(/\s/g, "");
  if (!isEditMode) {
    if (!rawAadhaar)                        e.aadhaar = "Aadhaar is required";
    else if (!/^\d{12}$/.test(rawAadhaar))  e.aadhaar = "Must be exactly 12 digits";
  } else if (rawAadhaar && !/^\d{12}$/.test(rawAadhaar)) {
    e.aadhaar = "Must be exactly 12 digits";
  }

  if (form.gst_applicable && !form.gst_number.trim()) e.gst_number = "GST number required";
  if (form.tds_applicable && !form.tds_percentage)    e.tds_percentage = "TDS percentage required";

  const b = form.bank;
  if (!b.account_number) e.account_number = "Required";
  if (!b.account_name)   e.account_name   = "Required";
  if (!b.bank_name)      e.bank_name      = "Required";
  if (!b.branch_name)    e.branch_name    = "Required";
  if (!b.ifsc_code)      e.ifsc_code      = "Required";
  else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(b.ifsc_code.toUpperCase()))
    e.ifsc_code = "Invalid IFSC (e.g. SBIN0001234)";

  if (!form.files.aadhaar_doc) e.aadhaar_doc = "Aadhaar document is required";
  if (!form.files.cheque_doc) e.cheque_doc = "Cancelled cheque / passbook upload is required";

  return e;
}

// ─── makeStyles ───────────────────────────────────────────────────────────────
const useStyles = makeStyles((theme) => ({
  formWrap: {
    marginTop: theme.spacing(3),
  },
  sectionCard: {
    padding: theme.spacing(3),
    borderRadius: 12,
    border: `1px solid ${BORDER}`,
    backgroundColor: "#fff",
    marginBottom: theme.spacing(2.5),
  },
  sectionHeaderRow: {
    display: "flex", alignItems: "center",
    gap: theme.spacing(1.5), marginBottom: theme.spacing(2.5),
    paddingBottom: theme.spacing(2), borderBottom: `1px solid ${BORDER}`,
  },
  sectionAvatar: {
    backgroundColor: PRIMARY_LIGHTER, color: PRIMARY, width: 40, height: 40,
  },
  sectionTitle: { fontWeight: 700, color: PRIMARY, lineHeight: 1.2 },
  sectionSub:   { color: theme.palette.text.secondary, fontSize: 12 },

  // ── Service type radio ──
  serviceTypeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: theme.spacing(1),
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "repeat(2, 1fr)" },
  },
  serviceTypeCard: {
    border: `1.5px solid ${BORDER}`,
    borderRadius: 8,
    padding: theme.spacing(1.25, 1.5),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    cursor: "pointer",
    transition: "all 0.2s",
    "&:hover": { borderColor: PRIMARY_LIGHT, backgroundColor: PRIMARY_LIGHTER },
  },
  serviceTypeCardSelected: {
    borderColor: PRIMARY_LIGHT,
    backgroundColor: PRIMARY_LIGHTER,
  },
  serviceTypeLabel: {
    fontWeight: 500, fontSize: 13, color: PRIMARY,
  },

  // ── Toggle box ──
  toggleBox: {
    padding: theme.spacing(2), borderRadius: 8,
    border: `1px solid ${BORDER}`, marginBottom: theme.spacing(2),
  },

  // ── File upload ──
  fileCard: {
    border: `1.5px dashed ${BORDER}`, borderRadius: 8, padding: theme.spacing(2),
    cursor: "pointer", transition: "all 0.2s", display: "flex",
    alignItems: "center", gap: theme.spacing(1.5), minHeight: 64,
    "&:hover": { borderColor: PRIMARY_LIGHT, backgroundColor: PRIMARY_LIGHTER },
  },
  fileCardUploaded: { borderColor: PRIMARY_LIGHT, backgroundColor: "rgba(235,243,251,0.6)" },
  fileCardError: { borderColor: "#D32F2F", backgroundColor: "rgba(211,47,47,0.04)" },
  fileAvatar:         { width: 36, height: 36 },
  fileAvatarUploaded: { backgroundColor: "rgba(26,60,110,0.12)", color: PRIMARY },
  fileAvatarEmpty:    { backgroundColor: "rgba(46,117,182,0.08)", color: PRIMARY_LIGHT },
  fileAvatarError:    { backgroundColor: "rgba(211,47,47,0.08)", color: "#D32F2F" },

  // ── Draft banner ──
  draftBanner: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: theme.spacing(1.5, 2), borderRadius: 10,
    backgroundColor: WARN_BG, border: `1px solid ${WARN_BORDER}`,
    marginBottom: theme.spacing(2.5), gap: theme.spacing(1), flexWrap: "wrap",
  },
  draftBannerLeft:    { display: "flex", alignItems: "center", gap: theme.spacing(1) },
  draftBannerActions: { display: "flex", alignItems: "center", gap: theme.spacing(1) },
  resumeBtn: {
    backgroundColor: WARN_COLOR, color: "#fff", borderRadius: 8,
    textTransform: "none", fontWeight: 600, padding: theme.spacing(0.5, 2),
    boxShadow: "none",
    "&:hover": { backgroundColor: "#92400E", boxShadow: "none" },
  },
  discardBtn: {
    borderColor: WARN_BORDER, color: WARN_COLOR, borderRadius: 8,
    textTransform: "none", fontWeight: 600, padding: theme.spacing(0.5, 1.5),
  },

  // ── Action row ──
  actionRow: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    marginTop: theme.spacing(1), paddingBottom: theme.spacing(2),
  },
  saveDraftBtn: {
    borderColor: WARN_BORDER, color: WARN_COLOR, borderRadius: 10,
    textTransform: "none", fontWeight: 600, padding: theme.spacing(0.75, 2),
    backgroundColor: WARN_BG,
    "&:hover": { backgroundColor: "#FEF3C7", borderColor: WARN_BORDER },
    "&:disabled": { opacity: 0.5 },
  },
  submitBtn: {
    backgroundColor: SUCCESS_COLOR, borderRadius: 10, textTransform: "none",
    fontWeight: 600, padding: theme.spacing(0.75, 3.5), boxShadow: "none",
    "&:hover": { backgroundColor: SUCCESS_COLOR, filter: "brightness(0.92)", boxShadow: "none" },
  },

  // ── Success ──
  successWrap: {
    maxWidth: 520, margin: "0 auto", marginTop: theme.spacing(5),
    padding: theme.spacing(0, 2), textAlign: "center",
  },
  successAvatar: {
    width: 72, height: 72, backgroundColor: "rgba(27,138,90,0.1)",
    color: SUCCESS_COLOR, margin: "0 auto", marginBottom: theme.spacing(3),
  },
  summaryPaper: {
    padding: theme.spacing(2.5), borderRadius: 8,
    border: `1px solid ${BORDER}`, textAlign: "left", marginBottom: theme.spacing(3),
  },
  summaryRow: {
    display: "flex", justifyContent: "space-between", padding: theme.spacing(0.75, 0),
    borderBottom: `0.5px solid ${BORDER}`,
    "&:last-child": { borderBottom: "none" },
  },
  outlinedBtn: { borderColor: BORDER, color: PRIMARY_LIGHT, borderRadius: 8, textTransform: "none" },

  infoAlert: { borderRadius: 8, fontSize: 13, marginBottom: theme.spacing(2) },
}));

// ─── FileUploadCard ───────────────────────────────────────────────────────────
function SPFileCard({ label, required, fieldKey, file, error, uploading, onChange }) {
  const classes = useStyles();
  const id = `sp-upload-${fieldKey}`;
  const done = Boolean(file) && !uploading;
  return (
    <>
      <label
        htmlFor={id}
        className={[
          classes.fileCard,
          done ? classes.fileCardUploaded : "",
          (!done && error) ? classes.fileCardError : "",
        ].join(" ")}
        style={uploading ? { pointerEvents: "none", opacity: 0.7 } : undefined}
      >
        <input
          id={id} type="file" style={{ display: "none" }}
          accept=".pdf,.jpg,.jpeg,.png"
          disabled={uploading}
          onChange={(e) => onChange(fieldKey, e.target.files[0] || null)}
        />
        <Avatar className={[
          classes.fileAvatar,
          done ? classes.fileAvatarUploaded : (!done && error) ? classes.fileAvatarError : classes.fileAvatarEmpty,
        ].join(" ")}>
          {uploading ? <CircularProgress size={18} /> : done ? <Check style={{ fontSize: 18 }} /> : <CloudUpload style={{ fontSize: 18 }} />}
        </Avatar>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" style={{ fontWeight: 500, color: done ? PRIMARY : (!done && error) ? "#D32F2F" : undefined }}>
            {label}{required && <span style={{ color: "#D32F2F", marginLeft: 2 }}>*</span>}
          </Typography>
          <Typography variant="caption" color="textSecondary" noWrap display="block">
            {uploading ? "Uploading…" : done ? file.name : "PDF, JPG or PNG · max 10 MB"}
          </Typography>
        </div>
        {done && (
          <IconButton size="small"
            onClick={(e) => { e.preventDefault(); onChange(fieldKey, null); }}
            style={{ color: "#D32F2F" }}
          >
            <Delete style={{ fontSize: 16 }} />
          </IconButton>
        )}
      </label>
      {!done && error && (
        <Typography variant="caption" style={{ color: "#D32F2F", marginLeft: 2 }}>{error}</Typography>
      )}
    </>
  );
}

// ─── SectionHeader ────────────────────────────────────────────────────────────
function SectionHeader({ icon: Icon, title, subtitle }) {
  const classes = useStyles();
  return (
    <div className={classes.sectionHeaderRow}>
      <Avatar className={classes.sectionAvatar}>
        <Icon style={{ fontSize: 20 }} />
      </Avatar>
      <div>
        <Typography variant="subtitle1" className={classes.sectionTitle}>{title}</Typography>
        {subtitle && <Typography className={classes.sectionSub}>{subtitle}</Typography>}
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function ServiceProviderOnboarding({
  allExperienceCenters,
  setLoading,
  setSnackInfo,
  editId,
}) {
  const classes = useStyles();
  const isEditMode = Boolean(editId);

  const [form,       setForm]       = useState(INITIAL_SP);
  const [errors,     setErrors]     = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(false);
  const [loadingEditRecord, setLoadingEditRecord] = useState(isEditMode);

  // ── Draft state ───────────────────────────────────────────────────────────
  const [existingDraft,   setExistingDraft]   = useState(null);
  const [showDraftBanner, setShowDraftBanner] = useState(false);
  const [savingDraft,     setSavingDraft]     = useState(false);
  const [draftSavedAt,    setDraftSavedAt]    = useState(null);

  // ── Admin-managed Service Type options (falls back to the legacy fixed
  // list if the config list is empty, e.g. before an admin has added any) ──
  const [serviceTypeOptions, setServiceTypeOptions] = useState(FALLBACK_SERVICE_TYPES);

  useEffect(() => {
    (async () => {
      try {
        const res = await HttpService.getServiceTypeConfigs();
        const list = res?.data?.data || [];
        if (list.length > 0) {
          setServiceTypeOptions([
            ...list.map((s) => ({ value: s.name, label: s.name })),
            { value: "others", label: "Others" },
          ]);
        }
      } catch (_) { /* fall back to FALLBACK_SERVICE_TYPES */ }
    })();
  }, []);

  // ── Fetch SP drafts on mount (skipped in edit mode) ───────────────────────
  useEffect(() => {
    if (isEditMode) return;
    (async () => {
      try {
        const res = await HttpService.getServiceProviderDrafts();
        // GET /ex/service-providers/drafts
        const drafts = res?.data?.data || [];
        if (drafts.length > 0) {
          setExistingDraft(drafts[0]);
          setShowDraftBanner(true);
        }
      } catch (_) { /* silent */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load an existing provider into the form for editing ───────────────────
  useEffect(() => {
    if (!isEditMode) return;
    (async () => {
      try {
        const res = await HttpService.getServiceProvider(editId);
        const p = res?.data?.data;
        if (!p) {
          setSnackInfo("Service provider record not found", "error");
          return;
        }
        setForm({
          location_mappings: (p.location_mappings?.length ? p.location_mappings : [BLANK_LOCATION]).map((m) => ({
            experienceCenterId: m.experienceCenterId?._id || m.experienceCenterId || "",
            scopes: (m.scopes?.length ? m.scopes : [BLANK_SCOPE]).map((s) => ({
              service_type: s.service_type || "",
              service_type_other: s.service_type_other || "",
            })),
          })),
          name: p.name || "",
          email: p.email || "",
          mobile: p.mobile || "",
          pan_number: p.pan_number || "",
          aadhaar: "", // never returned by the API
          gst_applicable: !!p.gst_applicable,
          gst_number: p.gst_number || "",
          tds_applicable: !!p.tds_applicable,
          tds_percentage: p.tds_percentage ?? "",
          bank: p.bank || INITIAL_SP.bank,
          files: {
            aadhaar_doc: (p.documents || []).find((d) => d.doc_type === "aadhaar")
              ? { name: "Aadhaar (already uploaded)", s3Link: (p.documents.find((d) => d.doc_type === "aadhaar")).file_path, existing: true }
              : null,
            pan_doc: (p.documents || []).find((d) => d.doc_type === "pan")
              ? { name: "PAN (already uploaded)", s3Link: (p.documents.find((d) => d.doc_type === "pan")).file_path, existing: true }
              : null,
            cheque_doc: (p.documents || []).find((d) => d.doc_type === "cancelled_cheque")
              ? { name: "Cancelled Cheque (already uploaded)", s3Link: (p.documents.find((d) => d.doc_type === "cancelled_cheque")).file_path, existing: true }
              : null,
          },
        });
      } catch (err) {
        setSnackInfo(err?.response?.data?.message || "Failed to load service provider record", "error");
      } finally {
        setLoadingEditRecord(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  // ── Field helpers ─────────────────────────────────────────────────────────
  const set = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    setErrors((e) => { const n = { ...e }; delete n[key]; return n; });
  };
  const setBank = (key, val) => {
    setForm((f) => ({ ...f, bank: { ...f.bank, [key]: val } }));
    setErrors((e) => { const n = { ...e }; delete n[key]; return n; });
  };
  const setFile = (key, file) => {
    setForm((f) => ({ ...f, files: { ...f.files, [key]: file } }));
    setErrors((e) => { const n = { ...e }; delete n[key]; return n; });
  };

  const [uploadingFile, setUploadingFile] = useState({});

  const handleFileUpload = async (key, file) => {
    if (!file) { setFile(key, null); return; }
    if (file.size > 10 * 1024 * 1024) {
      setSnackInfo(`File "${file.name}" exceeds 10MB limit`, "error");
      return;
    }
    setUploadingFile((u) => ({ ...u, [key]: true }));
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await HttpService.uploadFile(formData);
      const s3Link = response.data?.s3Link || response.data?.url || response.data;
      setFile(key, {
        name: file.name,
        size: file.size,
        type: file.type,
        s3Link,
        uploadedAt: new Date().toISOString(),
      });
    } catch (err) {
      setSnackInfo(`Failed to upload "${file.name}": ${err?.response?.data?.message || err?.message || "Unknown error"}`, "error");
    } finally {
      setUploadingFile((u) => ({ ...u, [key]: false }));
    }
  };

  // ── Location / scope helpers ──────────────────────────────────────────────
  const addLocation = () => {
    setForm((f) => ({ ...f, location_mappings: [...f.location_mappings, { experienceCenterId: "", scopes: [{ ...BLANK_SCOPE }] }] }));
  };
  const removeLocation = (locIdx) => {
    setForm((f) => ({ ...f, location_mappings: f.location_mappings.filter((_, i) => i !== locIdx) }));
  };
  const setLocationEC = (locIdx, ecId) => {
    setForm((f) => {
      const mappings = [...f.location_mappings];
      mappings[locIdx] = { ...mappings[locIdx], experienceCenterId: ecId };
      return { ...f, location_mappings: mappings };
    });
    setErrors((e) => { const n = { ...e }; delete n[`loc_${locIdx}_ec`]; return n; });
  };
  const toggleLocationScope = (locIdx, serviceTypeValue) => {
    setForm((f) => {
      const mappings = [...f.location_mappings];
      const loc = mappings[locIdx];
      const has = loc.scopes.some((s) => s.service_type === serviceTypeValue);
      const scopes = has
        ? loc.scopes.filter((s) => s.service_type !== serviceTypeValue)
        : [...loc.scopes.filter((s) => s.service_type), { service_type: serviceTypeValue, service_type_other: "" }];
      mappings[locIdx] = { ...loc, scopes: scopes.length ? scopes : [{ ...BLANK_SCOPE }] };
      return { ...f, location_mappings: mappings };
    });
    setErrors((e) => { const n = { ...e }; delete n[`loc_${locIdx}_scope`]; return n; });
  };
  const setScopeField = (locIdx, scopeIdx, key, val) => {
    setForm((f) => {
      const mappings = [...f.location_mappings];
      const scopes = [...mappings[locIdx].scopes];
      scopes[scopeIdx] = { ...scopes[scopeIdx], [key]: val };
      mappings[locIdx] = { ...mappings[locIdx], scopes };
      return { ...f, location_mappings: mappings };
    });
  };

  // ── Draft handlers ────────────────────────────────────────────────────────
  const handleResumeDraft = () => {
    if (!existingDraft?.formData) return;
    setForm({ ...INITIAL_SP, ...existingDraft.formData, files: INITIAL_SP.files });
    setShowDraftBanner(false);
    setSnackInfo("Draft restored — documents must be re-uploaded", "info");
  };

  const handleDiscardDraft = async () => {
    setShowDraftBanner(false);
    if (!existingDraft?._id) return;
    try {
      await HttpService.deleteServiceProviderDraft(existingDraft._id);
      setExistingDraft(null);
    } catch (_) { /* silent */ }
  };

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      const payload = { ...form, files: {} };
      let res;
      if (existingDraft?._id) {
        res = await HttpService.updateServiceProviderDraft(existingDraft._id, payload);
        // PUT /ex/service-providers/update-draft/:id
      } else {
        res = await HttpService.createServiceProviderDraft(payload);
        // POST /ex/service-providers/draft
        if (res?.data?.data?._id) setExistingDraft(res.data.data);
      }
      setDraftSavedAt(new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }));
      setSnackInfo("Draft saved", "success");
    } catch (err) {
      setSnackInfo(err?.response?.data?.message || "Failed to save draft", "error");
    } finally {
      setSavingDraft(false);
    }
  };

  const deleteDraftAfterSubmit = async () => {
    if (!existingDraft?._id) return;
    try {
      await HttpService.deleteServiceProviderDraft(existingDraft._id);
    } catch (_) { /* silent */ }
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const errs = validateSP(form, isEditMode);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    try {
      setSubmitting(true);

      if (isEditMode) {
        const response = await HttpService.updateServiceProvider(editId, form);
        if (response.data.success) {
          setSnackInfo(response.data.message, "success");
          window.history.back();
        }
        return;
      }

      const response = await HttpService.onBoardServiceProvider(form);
      // POST /ex/service-providers/onboard
      if (response.data.success) {
        await deleteDraftAfterSubmit();
        setSubmitted(true);
        setSnackInfo(response.data.message, "success");
      }
    } catch (err) {
      setSnackInfo(err?.response?.data?.message || "Something went wrong", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const ifscValid    = /^[A-Z]{4}0[A-Z0-9]{6}$/.test(form.bank.ifsc_code.toUpperCase());
  const aadhaarValid = /^\d{12}$/.test(form.aadhaar.replace(/\s/g, ""));
  const scopeLabel = (s) => (s.service_type === "others" ? (s.service_type_other || "Others") : (serviceTypeOptions.find(o => o.value === s.service_type)?.label || s.service_type));
  const locationSummary = form.location_mappings
    .map((loc) => {
      const ec = allExperienceCenters.find((e) => e._id === loc.experienceCenterId);
      const scopes = loc.scopes.filter((s) => s.service_type).map(scopeLabel).join(", ");
      return ec ? `${ec.name} (${scopes || "—"})` : null;
    })
    .filter(Boolean)
    .join("; ");

  // ── EDIT MODE LOADING ─────────────────────────────────────────────────────
  if (loadingEditRecord) {
    return (
      <div style={{ textAlign: "center", paddingTop: 60 }}>
        <CircularProgress style={{ color: PRIMARY_LIGHT }} />
        <Typography variant="body2" color="textSecondary" style={{ marginTop: 12 }}>
          Loading service provider record…
        </Typography>
      </div>
    );
  }

  // ── SUCCESS ───────────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <Fade in>
        <div className={classes.successWrap}>
          <Avatar className={classes.successAvatar}>
            <CheckCircle style={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h5" style={{ fontWeight: 700, color: PRIMARY }} gutterBottom>
            Service Provider onboarded
          </Typography>
          <Typography color="textSecondary" style={{ marginBottom: 24 }}>
            <strong>{form.name}</strong> has been added — <strong>{locationSummary || "—"}</strong>. Pending verification.
          </Typography>
          <Paper className={classes.summaryPaper} variant="outlined">
            {[
              ["Locations & Scopes", locationSummary || "—"],
              ["Name",             form.name],
              ["Mobile",           form.mobile],
              ["PAN",              form.pan_number.toUpperCase()],
              ["Status",           "Pending Verification"],
            ].map(([k, v]) => (
              <div key={k} className={classes.summaryRow}>
                <Typography variant="body2" color="textSecondary">{k}</Typography>
                <Typography variant="body2" style={{ fontWeight: 500 }}>{v}</Typography>
              </div>
            ))}
          </Paper>
          <Button
            variant="outlined" className={classes.outlinedBtn}
            onClick={() => { setForm(INITIAL_SP); setSubmitted(false); setExistingDraft(null); }}
          >
            Onboard another provider
          </Button>
        </div>
      </Fade>
    );
  }

  // ── FORM ──────────────────────────────────────────────────────────────────
  return (
    <div className={classes.formWrap}>

      {/* Draft banner */}
      {showDraftBanner && existingDraft && (
        <Fade in>
          <div className={classes.draftBanner}>
            <div className={classes.draftBannerLeft}>
              <Restore style={{ color: WARN_COLOR, fontSize: 22 }} />
              <div>
                <Typography variant="body2" style={{ fontWeight: 600, color: WARN_COLOR }}>
                  You have an unsaved draft
                </Typography>
                <Typography variant="caption" style={{ color: WARN_COLOR, opacity: 0.8 }}>
                  {existingDraft.formData?.name
                    ? `Provider: ${existingDraft.formData.name}`
                    : "Saved earlier"}
                  {existingDraft.updatedAt
                    ? ` · ${new Date(existingDraft.updatedAt).toLocaleDateString("en-IN", {
                        day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
                      })}`
                    : ""}
                </Typography>
              </div>
            </div>
            <div className={classes.draftBannerActions}>
              <Button variant="outlined" size="small" className={classes.discardBtn}
                onClick={handleDiscardDraft}>Discard</Button>
              <Button variant="contained" size="small" className={classes.resumeBtn}
                startIcon={<Restore style={{ fontSize: 16 }} />}
                onClick={handleResumeDraft}>Resume draft</Button>
              <IconButton size="small" onClick={() => setShowDraftBanner(false)}
                style={{ color: WARN_COLOR, padding: 4 }}>
                <Close style={{ fontSize: 16 }} />
              </IconButton>
            </div>
          </div>
        </Fade>
      )}

      {/* ── Section 1: Locations & Scopes ── */}
      <Paper elevation={0} className={classes.sectionCard}>
        <SectionHeader icon={Business} title="Locations & Scopes"
          subtitle="This provider can be mapped to multiple locations, each with its own service types" />

        {errors.location_mappings && (
          <Typography variant="caption" style={{ color: "#D32F2F", display: "block", marginBottom: 8 }}>
            {errors.location_mappings}
          </Typography>
        )}

        {form.location_mappings.map((loc, locIdx) => (
          <Paper key={locIdx} variant="outlined" style={{ padding: 16, marginBottom: 16, borderColor: BORDER }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 12 }}>
              <FormControl fullWidth variant="outlined" size="small" error={!!errors[`loc_${locIdx}_ec`]}>
                <InputLabel>Location *</InputLabel>
                <Select
                  value={loc.experienceCenterId} label="Location *"
                  onChange={(e) => setLocationEC(locIdx, e.target.value)}
                >
                  {allExperienceCenters.map((ec) => (
                    <MenuItem key={ec._id} value={ec._id}>{ec.name} - {ec.location}</MenuItem>
                  ))}
                </Select>
                {errors[`loc_${locIdx}_ec`] && <FormHelperText>{errors[`loc_${locIdx}_ec`]}</FormHelperText>}
              </FormControl>
              {form.location_mappings.length > 1 && (
                <IconButton size="small" onClick={() => removeLocation(locIdx)} style={{ color: "#D32F2F" }}>
                  <Delete style={{ fontSize: 18 }} />
                </IconButton>
              )}
            </div>

            <Typography variant="body2" style={{ fontWeight: 600, color: PRIMARY, marginBottom: 8 }}>
              Scopes (Type of Service) *
            </Typography>
            {errors[`loc_${locIdx}_scope`] && (
              <Typography variant="caption" style={{ color: "#D32F2F", display: "block", marginBottom: 8 }}>
                {errors[`loc_${locIdx}_scope`]}
              </Typography>
            )}
            <div className={classes.serviceTypeGrid}>
              {serviceTypeOptions.map(({ value, label }) => {
                const selected = loc.scopes.some((s) => s.service_type === value);
                return (
                  <div
                    key={value}
                    className={[classes.serviceTypeCard, selected ? classes.serviceTypeCardSelected : ""].join(" ")}
                    onClick={() => toggleLocationScope(locIdx, value)}
                  >
                    <Checkbox
                      checked={selected} size="small" color="primary"
                      style={{ padding: 0, color: selected ? PRIMARY_LIGHT : undefined }}
                    />
                    <Typography className={classes.serviceTypeLabel}>{label}</Typography>
                  </div>
                );
              })}
            </div>

            {loc.scopes.filter((s) => s.service_type === "others").map((s, scopeIdx) => (
              <div key={s.service_type} style={{ marginTop: 16 }}>
                <TextField
                  fullWidth size="small" variant="outlined"
                  label="Specify service type *"
                  placeholder="e.g. Pest Control, Electrician…"
                  value={s.service_type_other}
                  onChange={(e) => setScopeField(locIdx, loc.scopes.indexOf(s), "service_type_other", e.target.value)}
                  error={!!errors[`loc_${locIdx}_scope_${loc.scopes.indexOf(s)}_other`]}
                  helperText={errors[`loc_${locIdx}_scope_${loc.scopes.indexOf(s)}_other`] || "Payment caps for each service type are managed centrally by finance/admin per location"}
                />
              </div>
            ))}
          </Paper>
        ))}

        <Button
          variant="outlined" startIcon={<Business />} size="small"
          onClick={addLocation}
          style={{ borderColor: BORDER, color: PRIMARY, borderRadius: 8, textTransform: "none" }}
        >
          Add another location
        </Button>
      </Paper>

      {/* ── Section 2: Identity ── */}
      <Paper elevation={0} className={classes.sectionCard}>
        <SectionHeader icon={Person} title="Provider Identity"
          subtitle="Personal details for this service provider" />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" variant="outlined" label="Full Name *"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              error={!!errors.name} helperText={errors.name}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person style={{ fontSize: 18, color: "#9E9E9E" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" variant="outlined" label="Mobile / WhatsApp *"
              value={form.mobile}
              onChange={(e) => set("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))}
              error={!!errors.mobile} helperText={errors.mobile}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography variant="caption" color="textSecondary">+91</Typography>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" variant="outlined" label="Email *"
              type="email" value={form.email}
              onChange={(e) => set("email", e.target.value)}
              error={!!errors.email} helperText={errors.email}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* ── Section 3: Tax & Compliance ── */}
      <Paper elevation={0} className={classes.sectionCard}>
        <SectionHeader icon={VerifiedUser} title="Tax & Compliance"
          subtitle="PAN is mandatory. Aadhaar is encrypted at rest." />
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" variant="outlined" label="PAN Number *"
              placeholder="ABCDE1234F"
              value={form.pan_number}
              onChange={(e) => set("pan_number", e.target.value.toUpperCase())}
              error={!!errors.pan_number}
              helperText={errors.pan_number || "10-character PAN"}
              inputProps={{ maxLength: 10, style: { letterSpacing: 2 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AssignmentInd style={{ fontSize: 18, color: "#9E9E9E" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" variant="outlined" label="Aadhaar Number *"
              placeholder="12 digits" value={form.aadhaar}
              onChange={(e) => set("aadhaar", e.target.value.replace(/\D/g, "").slice(0, 12))}
              error={!!errors.aadhaar}
              helperText={errors.aadhaar || "Stored encrypted — never visible after save"}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <VisibilityOff style={{ fontSize: 18, color: "#9E9E9E" }} />
                  </InputAdornment>
                ),
                endAdornment: aadhaarValid && (
                  <InputAdornment position="end">
                    <Check style={{ fontSize: 18, color: SUCCESS_COLOR }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        <Divider style={{ marginBottom: 16 }} />

        {/* GST */}
        <div className={classes.toggleBox}>
          <FormControlLabel
            control={
              <Switch checked={form.gst_applicable} color="primary"
                onChange={(e) => set("gst_applicable", e.target.checked)} />
            }
            label={
              <div>
                <Typography variant="body2" style={{ fontWeight: 500 }}>GST Applicable</Typography>
                <Typography variant="caption" color="textSecondary">Toggle if provider is GST registered</Typography>
              </div>
            }
          />
          <Collapse in={form.gst_applicable}>
            <div style={{ marginTop: 16 }}>
              <TextField
                fullWidth size="small" variant="outlined" label="GST Number *"
                placeholder="27AAPFU0939F1ZV"
                value={form.gst_number}
                onChange={(e) => set("gst_number", e.target.value.toUpperCase())}
                error={!!errors.gst_number}
                helperText={errors.gst_number || "15-character GST number"}
                inputProps={{ maxLength: 15, style: { letterSpacing: 1 } }}
              />
            </div>
          </Collapse>
        </div>

        {/* TDS */}
        <div className={classes.toggleBox} style={{ marginBottom: 0 }}>
          <FormControlLabel
            control={
              <Switch checked={form.tds_applicable} color="primary"
                onChange={(e) => set("tds_applicable", e.target.checked)} />
            }
            label={
              <div>
                <Typography variant="body2" style={{ fontWeight: 500 }}>TDS Applicable</Typography>
                <Typography variant="caption" color="textSecondary">Enable for TDS deduction on payments</Typography>
              </div>
            }
          />
          <Collapse in={form.tds_applicable}>
            <div style={{ marginTop: 16 }}>
              <TextField
                size="small" variant="outlined" label="TDS Percentage *"
                type="number" inputProps={{ min: 0, max: 100, step: 0.5 }}
                value={form.tds_percentage}
                onChange={(e) => set("tds_percentage", e.target.value)}
                error={!!errors.tds_percentage}
                helperText={errors.tds_percentage || "e.g. 2 for 2%"}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                style={{ width: 200 }}
              />
            </div>
          </Collapse>
        </div>
      </Paper>

      {/* ── Section 4: Bank Details ── */}
      <Paper elevation={0} className={classes.sectionCard}>
        <SectionHeader icon={AccountBalance} title="Bank Account Details"
          subtitle="Used for payment transfers — must match provider's records" />
        <Alert severity="info" className={classes.infoAlert} icon={<Info style={{ fontSize: 18 }} />}>
          Enter details exactly as they appear on the cancelled cheque or passbook.
        </Alert>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" variant="outlined" label="Account Holder Name *"
              placeholder="As per bank records"
              value={form.bank.account_name}
              onChange={(e) => setBank("account_name", e.target.value)}
              error={!!errors.account_name} helperText={errors.account_name}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" variant="outlined" label="Account Number *"
              value={form.bank.account_number}
              onChange={(e) => setBank("account_number", e.target.value.replace(/\D/g, ""))}
              error={!!errors.account_number} helperText={errors.account_number}
              inputProps={{ style: { letterSpacing: 2 } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" variant="outlined" label="Bank Name *"
              placeholder="e.g. State Bank of India"
              value={form.bank.bank_name}
              onChange={(e) => setBank("bank_name", e.target.value)}
              error={!!errors.bank_name} helperText={errors.bank_name}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" variant="outlined" label="Branch Name *"
              value={form.bank.branch_name}
              onChange={(e) => setBank("branch_name", e.target.value)}
              error={!!errors.branch_name} helperText={errors.branch_name}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" variant="outlined" label="IFSC Code *"
              placeholder="SBIN0001234"
              value={form.bank.ifsc_code}
              onChange={(e) => setBank("ifsc_code", e.target.value.toUpperCase())}
              error={!!errors.ifsc_code}
              helperText={errors.ifsc_code || "11-character IFSC code"}
              inputProps={{ maxLength: 11, style: { letterSpacing: 2 } }}
              InputProps={{
                endAdornment: ifscValid && (
                  <InputAdornment position="end">
                    <Check style={{ fontSize: 18, color: SUCCESS_COLOR }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Account Type</InputLabel>
              <Select
                value={form.bank.account_type} label="Account Type"
                onChange={(e) => setBank("account_type", e.target.value)}
              >
                {ACCOUNT_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>{t.toUpperCase()}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* ── Section 5: Documents ── */}
      <Paper elevation={0} className={classes.sectionCard}>
        <SectionHeader icon={CloudUpload} title="Documents"
          subtitle="Aadhaar and cancelled cheque / bank passbook are mandatory. PAN is optional." />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <SPFileCard
              label="Aadhaar Card" required fieldKey="aadhaar_doc"
              file={form.files.aadhaar_doc}
              error={errors.aadhaar_doc}
              uploading={uploadingFile.aadhaar_doc}
              onChange={handleFileUpload}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SPFileCard
              label="PAN Card" fieldKey="pan_doc"
              file={form.files.pan_doc}
              uploading={uploadingFile.pan_doc}
              onChange={handleFileUpload}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SPFileCard
              label="Cancelled Cheque / Bank Passbook" required fieldKey="cheque_doc"
              file={form.files.cheque_doc}
              error={errors.cheque_doc}
              uploading={uploadingFile.cheque_doc}
              onChange={handleFileUpload}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* ── Action row ── */}
      <div className={classes.actionRow}>
        <Button
          variant="outlined"
          startIcon={
            savingDraft
              ? <CircularProgress size={14} style={{ color: WARN_COLOR }} />
              : <Save style={{ fontSize: 16 }} />
          }
          onClick={handleSaveDraft}
          disabled={savingDraft}
          className={classes.saveDraftBtn}
        >
          {savingDraft ? "Saving…" : draftSavedAt ? `Saved ${draftSavedAt}` : "Save draft"}
        </Button>

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || Object.values(uploadingFile).some(Boolean)}
          className={classes.submitBtn}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <Check />}
        >
          {submitting ? "Submitting…" : "Submit & Onboard"}
        </Button>
      </div>

    </div>
  );
}